import {
  PERMISSION_ACTIONS,
  PERMISSION_GROUPS,
  PERMISSION_MODULES,
  PERMISSIONS,
  SPECIFIC_PERMISSIONS,
} from "@artisancode/api-types";
import {
  CheckCheck,
  History,
  Package,
  Receipt,
  Settings,
  Shield,
  Wallet,
  X,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

import { PermissionTable } from "./permission-table";

import type {
  Permission,
  PermissionModule,
  SpecificPermission,
} from "@artisancode/api-types";

const MODULE_LABEL_LOOKUP = new Map(
  PERMISSION_MODULES.map((m) => [m.key, m.label]),
);

function getGroupIcon(groupKey: string) {
  switch (groupKey) {
    case "sales":
      return (
        <Receipt className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
      );
    case "finance":
      return <Wallet className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    case "master_data":
      return <Package className="h-4 w-4 text-amber-600 dark:text-amber-400" />;
    case "audit":
      return (
        <History className="h-4 w-4 text-purple-600 dark:text-purple-400" />
      );
    case "settings":
      return (
        <Settings className="h-4 w-4 text-slate-600 dark:text-slate-400" />
      );
    default:
      return null;
  }
}

export function PermissionsSection({
  permissions,
  disabled,
  onToggleModule,
  onToggleGroup,
  onToggleAll,
  onTogglePermission,
}: {
  permissions: Set<Permission>;
  disabled: boolean;
  onToggleModule: (moduleKey: string, checked: boolean) => void;
  onToggleGroup: (
    moduleKeys: readonly PermissionModule[],
    specificPermissions: readonly SpecificPermission[],
    checked: boolean,
  ) => void;
  onToggleAll: (checked: boolean) => void;
  onTogglePermission: (p: Permission) => void;
}) {
  const allSelected = permissions.size === PERMISSIONS.length;
  const noneSelected = permissions.size === 0;

  return (
    <div className="space-y-6">
      {/* Top Header & Global Actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Hak Akses Role</h2>
            <Badge variant="outline" className="tabular-nums">
              <span className="font-semibold text-primary">
                {permissions.size}
              </span>
              <span className="mx-1 text-muted-foreground">/</span>
              <span>{PERMISSIONS.length} aktif</span>
            </Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Dikelompokkan berdasarkan fitur utama. Mencakup aksi operasional
            CRUD serta wewenang otorisasi khusus (Supervisor / Manajer).
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || allSelected}
            onClick={() => onToggleAll(true)}
            className="text-xs"
          >
            <CheckCheck className="mr-1 h-3.5 w-3.5" />
            Pilih Semua
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled || noneSelected}
            onClick={() => onToggleAll(false)}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="mr-1 h-3.5 w-3.5" />
            Kosongkan
          </Button>
        </div>
      </div>

      <Separator />

      {/* Feature Groups */}
      <div className="space-y-6">
        {PERMISSION_GROUPS.map((group) => {
          const groupModules = PERMISSION_MODULES.filter((m) =>
            (group.modules as readonly string[]).includes(m.key),
          );

          const groupCrudPerms = group.modules.flatMap((mod) =>
            PERMISSION_ACTIONS.map((a) => `${mod}.${a.key}` as Permission),
          );

          const groupSpecifics = SPECIFIC_PERMISSIONS.filter((sp) =>
            (
              group.specificPermissions as readonly string[] | undefined
            )?.includes(sp.key),
          );

          const groupSpecificPerms = groupSpecifics.map(
            (sp) => sp.key as Permission,
          );

          const allGroupPerms: Permission[] = [
            ...groupCrudPerms,
            ...groupSpecificPerms,
          ];

          const selectedCount = allGroupPerms.filter((p) =>
            permissions.has(p),
          ).length;

          const isAllChecked = selectedCount === allGroupPerms.length;
          const isSomeChecked = selectedCount > 0 && !isAllChecked;
          const groupCheckboxState = isAllChecked
            ? true
            : isSomeChecked
              ? "indeterminate"
              : false;

          const selectedSpecificCount = groupSpecificPerms.filter((p) =>
            permissions.has(p),
          ).length;

          return (
            <div
              key={group.key}
              className="rounded-xl border border-border/80 bg-card p-4 sm:p-5 shadow-xs transition-shadow space-y-4"
            >
              {/* Group Header */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-lg border border-border/70 bg-muted/50 p-2 shadow-2xs">
                    {getGroupIcon(group.key)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground">
                        {group.label}
                      </h3>
                      <Badge
                        variant={
                          isAllChecked
                            ? "default"
                            : isSomeChecked
                              ? "secondary"
                              : "outline"
                        }
                        className="text-[10px] tabular-nums"
                      >
                        {selectedCount}/{allGroupPerms.length} dipilih
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {group.description}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-start sm:self-auto pl-11 sm:pl-0">
                  <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground cursor-pointer select-none">
                    <Checkbox
                      disabled={disabled}
                      checked={groupCheckboxState}
                      onCheckedChange={() =>
                        onToggleGroup(
                          group.modules,
                          group.specificPermissions ?? [],
                          !isAllChecked,
                        )
                      }
                    />
                    <span>Pilih Semua di Grup Ini</span>
                  </label>
                </div>
              </div>

              {/* Group Modules CRUD Table */}
              <PermissionTable
                modules={groupModules}
                permissions={permissions}
                disabled={disabled}
                onToggleModule={onToggleModule}
                onTogglePermission={onTogglePermission}
              />

              {/* Specific Actions Sub-section */}
              {groupSpecifics.length > 0 && (
                <div className="mt-5 pt-4 border-t border-border/60">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="rounded-md bg-amber-500/10 p-1 text-amber-600 dark:text-amber-400">
                        <Shield className="h-3.5 w-3.5" />
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                          Wewenang & Kebijakan Khusus
                        </h4>
                        <p className="text-[11px] text-muted-foreground">
                          Otorisasi tingkat lanjut dan tindakan sensitif di luar
                          operasional CRUD harian (level Supervisor / Manajer).
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={
                        selectedSpecificCount === groupSpecifics.length
                          ? "default"
                          : selectedSpecificCount > 0
                            ? "secondary"
                            : "outline"
                      }
                      className="text-[10px] tabular-nums self-start sm:self-auto shrink-0"
                    >
                      {selectedSpecificCount}/{groupSpecifics.length} wewenang
                      aktif
                    </Badge>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                    {groupSpecifics.map((sp) => {
                      const isChecked = permissions.has(sp.key);
                      return (
                        <label
                          key={sp.key}
                          className={cn(
                            "flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-all select-none",
                            isChecked
                              ? "border-primary/40 bg-primary/[0.03] shadow-2xs"
                              : "border-border/70 bg-card hover:bg-muted/40",
                            disabled && "cursor-not-allowed opacity-60",
                          )}
                        >
                          <div className="pt-0.5">
                            <Checkbox
                              disabled={disabled}
                              checked={isChecked}
                              onCheckedChange={() => onTogglePermission(sp.key)}
                            />
                          </div>
                          <div className="space-y-1 min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-foreground">
                                {sp.label}
                              </span>
                              <span className="rounded bg-muted px-1.5 py-0.2 text-[9px] font-medium text-muted-foreground">
                                {MODULE_LABEL_LOOKUP.get(sp.moduleKey) ??
                                  sp.moduleKey}
                              </span>
                            </div>
                            <p className="text-[11px] text-muted-foreground leading-relaxed">
                              {sp.description}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
